import {
  bookToTaxCodeDescriptionColumnDefinition,
  countryTaxTypeDescriptionColumnDefinition,
  descriptionColumnDefinition,
  disallowedAmountColumnDefinition,
  disallowedPercentageColumnDefinition,
  enabledFlagColumnDefinition,
  exceptionAccountColumnDefinition,
  exceptionBasisColumnDefinition,
  exclusionPercentageColumnDefinition,
  oneByOneBalanceColumnDefinition,
  bookToTaxDifferenceDescriptionColumnDefinition,
  ytdAmountColumnDefinition,
  exceptionIdColumnDefinition,
  connectionsColumnDefinition,
  businessUnitsColumnDefinition,
  excludedCompanyCodesColumnDefinition,
  includeAllApplicableCountryTaxTypesColumnDefinition,
  countryTaxTypesColumnDefinition,
  adjustmentTypeColumnDefinition,
  bookTypeColumnDefinition,
} from '../columnDefinitions';
import BookToTaxDifferencesCountryTaxTypeRenderer from '../cellRenderers/bookToTaxDifferencesCountryTaxTypeRenderer';
import { ALL_OPTION, countryTaxTypes, US_COUNTRY_TAX_TYPE_REGEX, exceptionBasisTypes } from '../constants';
import { alignCenterCellStyle } from '@client-shared/ui-agGrid';
import { PercentageOrRawValueFormatter } from '../valueFormatters/percentageFormatters';

export const calcSettingsBookToTaxDifferencesColumnDefs = [
  {
    ...countryTaxTypeDescriptionColumnDefinition,
    headerName: 'Country Tax Type',
  },
  bookToTaxDifferenceDescriptionColumnDefinition,
  exceptionAccountColumnDefinition,
  exceptionBasisColumnDefinition,
  disallowedPercentageColumnDefinition,
];

export const adjustmentsTabBookToTaxDifferencesColumnDefs = [
  {
    ...countryTaxTypeDescriptionColumnDefinition,
    headerName: 'Country Tax Type',
  },
  adjustmentTypeColumnDefinition,
  descriptionColumnDefinition,
  { ...exceptionAccountColumnDefinition, field: 'account' },
  bookTypeColumnDefinition,
  oneByOneBalanceColumnDefinition,
  { ...ytdAmountColumnDefinition, headerName: 'YTD Balance' },
  exclusionPercentageColumnDefinition,
  { ...disallowedAmountColumnDefinition, field: 'disallowedAmt' },
];

const US_FEDERAL = countryTaxTypes.US2;

const isCountryTypeValidForExceptionBasis = (countryTaxType, exceptionBasis) => {
  if ([exceptionBasisTypes.P, exceptionBasisTypes.T].includes(exceptionBasis)) {
    return !(US_COUNTRY_TAX_TYPE_REGEX.test(countryTaxType)) || countryTaxType === US_FEDERAL;
  }

  if ([exceptionBasisTypes.O, exceptionBasisTypes.G].includes(exceptionBasis)) {
    return !US_COUNTRY_TAX_TYPE_REGEX.test(countryTaxType);
  }

  if ([exceptionBasisTypes.M, exceptionBasisTypes.N].includes(exceptionBasis)) {
    return US_COUNTRY_TAX_TYPE_REGEX.test(countryTaxType) && countryTaxType !== US_FEDERAL;
  }

  if (exceptionBasis === exceptionBasisTypes.S) {
    return countryTaxType === US_FEDERAL;
  }

  return exceptionBasis === exceptionBasisTypes.C || exceptionBasis === exceptionBasisTypes.D;
};

const cttValueGetter = ({ data, countryTaxType }) => {
  if (!data.countryTaxTypes) {
    return false;
  }

  if (data.countryTaxTypes[0] !== ALL_OPTION) {
    return data.countryTaxTypes.includes(countryTaxType);
  }

  return isCountryTypeValidForExceptionBasis(countryTaxType, data.exceptionBasis);
};

export const getCountryTaxTypeColumnDefinitionForBookToTaxDifferences = ({ countryTaxType, description }) => ({
  headerName: countryTaxType,
  field: countryTaxType,
  colId: countryTaxType,
  width: 60,
  minWidth: 60,
  ...alignCenterCellStyle,
  headerTooltip: description,
  valueGetter: ({ data }) => cttValueGetter({ data, countryTaxType }),
  cellRenderer: BookToTaxDifferencesCountryTaxTypeRenderer,
  cellRendererParams: {
    editable: () => false,
  },
  getQuickFilterText: function getQuickFilterText({ value }) {
    return value ? `${countryTaxType}` : '';
  },
});

export const enabledFlagColumnDefinitionForBookToTaxDifferences = {
  ...enabledFlagColumnDefinition,
  headerName: 'Enabled',
  width: 80,
  minWidth: 80,
};

export const bookToTaxCodeDescriptionColumnDefinitionForBookToTaxDifferences = {
  ...bookToTaxCodeDescriptionColumnDefinition,
  width: 130,
  minWidth: 130,
};

export const disallowedPercentageColumnDefinitionForBookToTaxDifferences = {
  ...disallowedPercentageColumnDefinition,
  valueFormatter: PercentageOrRawValueFormatter,
};

const baseColumnDefinitions = [
  enabledFlagColumnDefinitionForBookToTaxDifferences,
  exceptionAccountColumnDefinition,
  bookToTaxDifferenceDescriptionColumnDefinition,
  exceptionBasisColumnDefinition,
  disallowedPercentageColumnDefinitionForBookToTaxDifferences,
  bookToTaxCodeDescriptionColumnDefinitionForBookToTaxDifferences,
  connectionsColumnDefinition,
  businessUnitsColumnDefinition,
  excludedCompanyCodesColumnDefinition,
  includeAllApplicableCountryTaxTypesColumnDefinition,
];

const isUsCountryTaxType = countryTaxType => countryTaxType.startsWith('US');

const sortAndGroupCountryTaxTypes = allCountryTaxTypes => {
  const usCountryTaxTypes = [
    ...allCountryTaxTypes
      .filter(({ countryTaxType }) => countryTaxType === countryTaxTypes.US1
        || countryTaxType === countryTaxTypes.US2
      )
      .sort((a, b) => a.countryTaxType.localeCompare(b.countryTaxType)),
    ...allCountryTaxTypes
      .filter(({ countryTaxType }) => isUsCountryTaxType(countryTaxType)
        && countryTaxType !== countryTaxTypes.US1
        && countryTaxType !== countryTaxTypes.US2
      )
      .sort((a, b) => a.countryTaxType.localeCompare(b.countryTaxType)),
  ];

  const nonUsCountryTaxTypes = allCountryTaxTypes
    .filter(({ countryTaxType }) => !isUsCountryTaxType(countryTaxType))
    .sort((a, b) => a.countryTaxType.localeCompare(b.countryTaxType));

  return {
    usCountryTaxTypes,
    nonUsCountryTaxTypes,
  };
};

export const getGroupedCountryTaxTypeColumns = (params, getCountryTaxTypeColumnDefinition) => {
  const {
    usCountryTaxTypes,
    nonUsCountryTaxTypes,
  } = sortAndGroupCountryTaxTypes(params.countryTaxTypes);

  return [
    {
      headerName: 'US Country Tax Types',
      children: usCountryTaxTypes
        .map(countryTaxType => getCountryTaxTypeColumnDefinition({
          ...params,
          ...countryTaxType,
        })),
    },
    {
      headerName: 'Non US Country Tax Types',
      children: nonUsCountryTaxTypes
        .map(countryTaxType => getCountryTaxTypeColumnDefinition({
          ...params,
          ...countryTaxType,
        })),
    },
  ];
};

export const getBookToTaxDifferencesColumnDefinitions = ({ countryTaxTypes }) => ([
  ...baseColumnDefinitions,
  ...getGroupedCountryTaxTypeColumns(
    { countryTaxTypes },
    getCountryTaxTypeColumnDefinitionForBookToTaxDifferences
  ),
]);

export const bookToTaxDifferencesColumnDefintionsForImport = [
  enabledFlagColumnDefinitionForBookToTaxDifferences,
  exceptionAccountColumnDefinition,
  bookToTaxDifferenceDescriptionColumnDefinition,
  exceptionBasisColumnDefinition,
  disallowedPercentageColumnDefinitionForBookToTaxDifferences,
  bookToTaxCodeDescriptionColumnDefinitionForBookToTaxDifferences,
  connectionsColumnDefinition,
  businessUnitsColumnDefinition,
  excludedCompanyCodesColumnDefinition,
  countryTaxTypesColumnDefinition,
  exceptionIdColumnDefinition,
];
